import { useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { IonIcon } from '@ionic/react';
import { timeOutline } from 'ionicons/icons';
import { format, parse } from 'date-fns';
import { FormEvent } from 'primereact/ts-helpers';
import { useMask } from '@react-input/mask';
import { Popover } from 'react-tiny-popover';
import Button from '../../../../Button/Button';
import { TimeInputControl } from '../../../../../types/types';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import type { GridInputBaseProps } from '../../types';
import {
  TIME_RANGE_MASK_OPTIONS,
  TIME_MASK_OPTIONS,
  TIME_FILTER_DATE_VALUES,
  TIME_INPUT_FORMAT,
  TIME_API_FORMAT,
  TIME_MASK,
  TIME_RANGE_MASK,
} from '../../../../../constants';
import { useMinMaxDateTimeValue } from '../../hooks/useMinMaxDateTimeValue';
import { parseStrictTimeValue } from '../../utils/parseStrictTimeValue';
import { normalizeTimeSeconds } from '../../utils/normalizeTimeSeconds';
import Input from '../../../../Input/Input';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';

type TimeRangePickerProps = GridInputBaseProps & {
  control: TimeInputControl;
};

const TimeRangePicker = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  name,
  error,
  setError,
  disabled,
  isColumnFilter,
  control,
}: TimeRangePickerProps) => {
  // Emit value to both change and touch in the same moment — non-text controls
  // treat `touch` as an undebounced equivalent of `change`. `propagateHandler`
  // defaults to true; pass false for programmatic writes (mount defaults etc.)
  // to skip firing the field-level handlers in FormHandlersContext.
  const emitChange = (v: string, propagateHandler: boolean = true) => {
    onChange(v, propagateHandler);
    if (propagateHandler) {
      onTouch?.(v);
    }
  };
  const { t } = useTranslate();
  const rangeMaskRef = useMask(TIME_RANGE_MASK_OPTIONS);
  const fromMaskRef = useMask(TIME_MASK_OPTIONS);
  const toMaskRef = useMask(TIME_MASK_OPTIONS);
  const [from, setFrom] = useState(new Date());
  const [to, setTo] = useState(new Date());
  const [displayedFromTime, setDisplayedFromTime] = useState('');
  const [displayedToTime, setDisplayedToTime] = useState('');
  const [displayedTimeRange, setDisplayedTimeRange] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { minValue, maxValue } = useMinMaxDateTimeValue(
    control.min,
    control.max,
    true
  );

  useEffect(() => {
    let newFromTime = new Date();
    let newToTime = new Date();
    let hasCorrectValue = false;
    if (value) {
      const splitted = value.split(',', 2);
      const parsedFrom = splitted[0]
        ? parseStrictTimeValue(normalizeTimeSeconds(splitted[0]))
        : null;
      const parsedTo = splitted[1]
        ? parseStrictTimeValue(normalizeTimeSeconds(splitted[1]))
        : null;
      if (parsedFrom && parsedTo) {
        hasCorrectValue = true;
        newFromTime = parsedFrom;
        newToTime = parsedTo;
      } else {
        // Invalid/incomplete range — ignore: show & submit empty.
        emitChange('', false);
        setError(null);
      }
    }
    newFromTime.setFullYear(...TIME_FILTER_DATE_VALUES);
    newToTime.setFullYear(...TIME_FILTER_DATE_VALUES);
    if (!hasCorrectValue) {
      if (minValue) {
        if (newFromTime < minValue) {
          newFromTime = new Date(minValue.getTime());
        }
        if (newToTime < minValue) {
          newToTime = new Date(minValue.getTime());
        }
      }
      if (maxValue) {
        if (newFromTime > maxValue) {
          newFromTime = new Date(maxValue.getTime());
        }
        if (newToTime > maxValue) {
          newToTime = new Date(maxValue.getTime());
        }
      }
    }
    setFrom(newFromTime);
    setTo(newToTime);
    const shouldColumnFilterPrefill = isColumnFilter && !value;
    if (hasCorrectValue || shouldColumnFilterPrefill) {
      checkMinMaxError(newFromTime, newToTime, minValue, maxValue);
      const readableFromString = format(newFromTime, TIME_INPUT_FORMAT);
      const readableToString = format(newToTime, TIME_INPUT_FORMAT);
      if (isColumnFilter) {
        setDisplayedFromTime(readableFromString);
        setDisplayedToTime(readableToString);
      } else {
        setDisplayedTimeRange(`${readableFromString} - ${readableToString}`);
      }
      if (isColumnFilter || hasCorrectValue) {
        const timeString = `${format(newFromTime, TIME_API_FORMAT)},${format(newToTime, TIME_API_FORMAT)}`;
        emitChange(timeString, false);
      }
    } else if (isColumnFilter) {
      setDisplayedFromTime('');
      setDisplayedToTime('');
    } else {
      setDisplayedTimeRange('');
    }
    //TODO: t in dependencies is a temporary fix for error messages not showing
    // It will probably not be needed after client-side routing merge
  }, [t]);

  const manualFromTimeChange = (v) => {
    if (v === TIME_MASK) {
      v = '';
    }
    setDisplayedFromTime(v);
    setError(null);
    if (v === '') {
      emitChange(`,${format(to, TIME_API_FORMAT)}`);
    } else {
      const newFromTime = parse(v, TIME_INPUT_FORMAT, new Date());
      if (newFromTime.getDate()) {
        newFromTime.setFullYear(...TIME_FILTER_DATE_VALUES);
        setFrom(newFromTime);
        const timeString = `${format(newFromTime, TIME_API_FORMAT)},${format(to, TIME_API_FORMAT)}`;
        emitChange(timeString);
      }
    }
  };

  const manualToTimeChange = (v) => {
    if (v === TIME_MASK) {
      v = '';
    }
    setDisplayedToTime(v);
    setError(null);
    if (v === '') {
      emitChange(`${format(from, TIME_API_FORMAT)},`);
    } else {
      const newToTime = parse(v, TIME_INPUT_FORMAT, new Date());
      if (newToTime.getDate()) {
        newToTime.setFullYear(...TIME_FILTER_DATE_VALUES);
        setTo(newToTime);
        const timeString = `${format(from, TIME_API_FORMAT)},${format(newToTime, TIME_API_FORMAT)}`;
        emitChange(timeString);
      }
    }
  };

  const manualTimeRangeChange = (v) => {
    if (v === TIME_RANGE_MASK) {
      v = '';
    }
    setDisplayedTimeRange(v);
    setError(null);
    if (v === '') {
      emitChange('');
    } else {
      const splitted = v.split(' - ', 2);
      if (splitted[0] && splitted[1]) {
        const newFromTime = parse(splitted[0], TIME_INPUT_FORMAT, new Date());
        const newToTime = parse(splitted[1], TIME_INPUT_FORMAT, new Date());
        if (
          newFromTime.getDate() &&
          newToTime.getDate() &&
          newFromTime <= newToTime
        ) {
          newFromTime.setFullYear(...TIME_FILTER_DATE_VALUES);
          newToTime.setFullYear(...TIME_FILTER_DATE_VALUES);
          setFrom(newFromTime);
          setTo(newToTime);
          const timeString = `${format(newFromTime, TIME_API_FORMAT)},${format(newToTime, TIME_API_FORMAT)}`;
          emitChange(timeString);
        }
      }
    }
  };

  const setInputAndApiValue = (fromTime: Date, toTime: Date) => {
    const fromApiString = format(fromTime, TIME_API_FORMAT);
    const toApiString = format(toTime, TIME_API_FORMAT);
    const timeString = `${fromApiString},${toApiString}`;
    if (!toTime || !fromTime || fromApiString <= toApiString) {
      setError(null);
    } else if (fromTime && toTime) {
      setError([t('errors.invalidBetweenError')]);
    }
    emitChange(timeString);
    const readableFromTimeString = format(fromTime, TIME_INPUT_FORMAT);
    const readableToTimeString = format(toTime, TIME_INPUT_FORMAT);
    if (isColumnFilter) {
      setDisplayedFromTime(readableFromTimeString);
      setDisplayedToTime(readableToTimeString);
      checkMinMaxError(fromTime, toTime, minValue, maxValue);
    } else {
      setDisplayedTimeRange(
        `${readableFromTimeString} - ${readableToTimeString}`
      );
    }
  };

  const checkMinMaxError = (
    fromTime: Date,
    toTime: Date,
    min: Date | null,
    max: Date | null
  ) => {
    if (min && (fromTime < min || toTime < min)) {
      const readableMinValue = format(min, TIME_INPUT_FORMAT);
      setError([t('errors.minValue').replace('{0}', readableMinValue)]);
    } else if (max && (fromTime > max || toTime > max)) {
      const readableMaxValue = format(max, TIME_INPUT_FORMAT);
      setError([t('errors.maxValue').replace('{0}', readableMaxValue)]);
    }
  };

  const onFromTimeChange = (e: FormEvent) => {
    const newFromTime = e.value as Date;
    newFromTime.setFullYear(...TIME_FILTER_DATE_VALUES);
    setFrom(newFromTime);
    setInputAndApiValue(newFromTime, to);
  };

  const onToTimeChange = (e: FormEvent) => {
    const newToTime = e.value as Date;
    newToTime.setFullYear(...TIME_FILTER_DATE_VALUES);
    setTo(newToTime);
    setInputAndApiValue(from, newToTime);
  };

  const onSubmit = () => {
    setError(null);
    if (from && to) {
      setInputAndApiValue(from, to);
      checkMinMaxError(from, to, minValue, maxValue);
    }
    setPopoverOpen(false);
  };

  const onFromBlur = () => {
    if (displayedFromTime !== '') {
      const newFromTime = parse(
        displayedFromTime,
        TIME_INPUT_FORMAT,
        new Date()
      );
      if (newFromTime.getDate()) {
        newFromTime.setFullYear(...TIME_FILTER_DATE_VALUES);
        checkMinMaxError(newFromTime, to, minValue, maxValue);
      } else {
        setError([t('errors.invalidTime')]);
      }
    }
    onBlur();
    onFocusOut?.();
  };

  const onToBlur = () => {
    if (displayedToTime !== '') {
      const newToTime = parse(displayedToTime, TIME_INPUT_FORMAT, new Date());
      if (newToTime.getDate()) {
        newToTime.setFullYear(...TIME_FILTER_DATE_VALUES);
        checkMinMaxError(from, newToTime, minValue, maxValue);
      } else {
        setError([t('errors.invalidTime')]);
      }
    }
    onBlur();
    onFocusOut?.();
  };

  const onBlur = () => {
    if (displayedFromTime && displayedToTime) {
      const fromTime = parse(displayedFromTime, TIME_INPUT_FORMAT, new Date());
      const toTime = parse(displayedToTime, TIME_INPUT_FORMAT, new Date());
      if (fromTime > toTime) {
        setError([t('errors.invalidBetweenError')]);
      }
    }
  };

  const onRangeBlur = () => {
    if (displayedTimeRange !== '') {
      const splitted = displayedTimeRange.split(' - ', 2);
      if (splitted[0] && splitted[1]) {
        const newFromTime = parse(splitted[0], TIME_INPUT_FORMAT, new Date());
        const newToTime = parse(splitted[1], TIME_INPUT_FORMAT, new Date());
        if (
          newFromTime.getDate() &&
          newToTime.getDate() &&
          newFromTime <= newToTime
        ) {
          newFromTime.setFullYear(...TIME_FILTER_DATE_VALUES);
          newToTime.setFullYear(...TIME_FILTER_DATE_VALUES);
          setError(null);
          checkMinMaxError(newFromTime, newToTime, minValue, maxValue);
        } else {
          setError([t('errors.invalidBetweenError')]);
        }
      } else {
        setError([t('errors.invalidBetweenError')]);
      }
    }
    onFocusOut?.();
  };

  const onClickOutside = () => {
    setPopoverOpen(false);
    checkMinMaxError(from, to, minValue, maxValue);
  };

  // Enter commits the picked range, Escape closes without committing; both are swallowed so they don't bubble (e.g. into a parent column-filter Apply handler). Guarded on popoverOpen so the same keys do nothing while only the input is focused.
  const onPopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!popoverOpen) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setPopoverOpen(false);
    }
  };

  const PickerElement = (
    <div className="timepicker-container range-filter-container">
      <Calendar
        value={from}
        onChange={onFromTimeChange}
        timeOnly
        inline
        hourFormat="24"
      />
      <span className="range-dash">-</span>
      <Calendar
        value={to}
        onChange={onToTimeChange}
        timeOnly
        inline
        hourFormat="24"
      />
    </div>
  );

  const InputElement = isColumnFilter ? (
    <>
      <div className="range-filter-container">
        <Input
          name={`${name}_from`}
          value={displayedFromTime}
          onChange={manualFromTimeChange}
          disabled={disabled}
          placeholder={t('timeInputPlaceholder')}
          inputRef={fromMaskRef}
          error={error}
          onBlur={onFromBlur}
          onFocus={onFocusIn}
          onKeyDown={onPopoverKeyDown}
          hideErrorText
        />
        <span className="range-dash">-</span>
        <Input
          name={`${name}_to`}
          value={displayedToTime}
          onChange={manualToTimeChange}
          disabled={disabled}
          placeholder={t('timeInputPlaceholder')}
          inputRef={toMaskRef}
          error={error}
          onBlur={onToBlur}
          onFocus={onFocusIn}
          onKeyDown={onPopoverKeyDown}
          hideErrorText
        />
      </div>
      <InputErrorMessage error={error} className="datetime-error" />
    </>
  ) : (
    <Input
      name={`${name}_from`}
      value={displayedTimeRange}
      onChange={manualTimeRangeChange}
      disabled={disabled}
      placeholder={`${t('timeInputPlaceholder')} - ${t('timeInputPlaceholder')}`}
      inputRef={rangeMaskRef}
      error={error}
      onBlur={onRangeBlur}
      onFocus={onFocusIn}
      onClick={() => setPopoverOpen(true)}
      hideErrorText
    />
  );
  return isColumnFilter ? (
    <div className="time-filter-column-container">
      {PickerElement}
      {InputElement}
    </div>
  ) : (
    <>
      <Popover
        isOpen={popoverOpen}
        positions={['bottom', 'top', 'left', 'right']}
        onClickOutside={onClickOutside}
        align="start"
        content={
          <div className="datetime-popover time-popover">
            {PickerElement}
            <div className="datetime-picker-footer">
              <Button onClick={onSubmit} fill="clear" color="secondary">
                {t('use')}
              </Button>
            </div>
          </div>
        }
      >
        <div className="datetime-picker-container">
          <Button
            fill="clear"
            color="secondary"
            disabled={disabled}
            onClick={() => setPopoverOpen((v) => !v)}
          >
            <IonIcon icon={timeOutline} />
          </Button>
          {InputElement}
        </div>
      </Popover>
      <InputErrorMessage error={error} className="datetime-error" />
    </>
  );
};

export default TimeRangePicker;
